import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDataQualitySummary } from '@/api/hooks/useDataQuality';
import DetectiveChips from '@/components/ai/DetectiveChips';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });

function completenessColor(pct: number): string {
  if (pct >= 95) return 'hsl(142 71% 45%)';
  if (pct >= 80) return 'hsl(48 96% 53%)';
  if (pct >= 60) return 'hsl(24 95% 53%)';
  return 'hsl(0 84% 60%)';
}

export default function DataQuality() {
  const { data, isLoading, error } = useDataQualitySummary();

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Data Quality</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fleet data integrity — field completeness, anomaly counters, quarterly trend.
        </p>
      </div>

      <DetectiveChips module="data-quality" />

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-800">
            Could not load data quality: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {(data?.anomalies ?? Array.from({ length: 4 }, () => null as unknown as null)).map(
          (a, i) => (
            <Card key={a?.label ?? i}>
              <CardHeader className="pb-1.5 pt-3">
                <CardTitle className="text-xs font-medium text-muted-foreground">
                  {a?.label ?? <Skeleton className="h-3 w-24" />}
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-3 pt-0">
                {isLoading || !a ? (
                  <Skeleton className="h-7 w-16" />
                ) : (
                  <div className="text-2xl font-semibold tabular-nums">
                    {NUM.format(a.count)}
                  </div>
                )}
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                  {a?.description ?? ''}
                </div>
              </CardContent>
            </Card>
          ),
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Field completeness — last 12 months</CardTitle>
          </CardHeader>
          <CardContent className="h-72 px-2 py-2">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data?.completeness ?? []}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 60 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <YAxis
                    type="category"
                    dataKey="field"
                    tick={{ fontSize: 10 }}
                    width={60}
                  />
                  <Tooltip
                    formatter={(v: number) => `${v.toFixed(1)}%`}
                  />
                  <Bar dataKey="completeness_pct">
                    {(data?.completeness ?? []).map((row, idx) => (
                      <Cell key={idx} fill={completenessColor(row.completeness_pct)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Quarterly DQ-flag rate (lower is better)</CardTitle>
          </CardHeader>
          <CardContent className="h-72 px-2 py-2">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data?.trend ?? []}
                  margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="quarter_start"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v: string) => v.slice(0, 7)}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    width={45}
                    tickFormatter={(v: number) => `${v.toFixed(0)}%`}
                  />
                  <Tooltip
                    formatter={(v: number) => `${v.toFixed(2)}%`}
                    labelFormatter={(v: string) => `Quarter starting ${v.slice(0, 7)}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="flagged_pct"
                    stroke="hsl(var(--destructive))"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
